/**
 * Where `level` and `tone` become placement and colour — once.
 *
 * `level` places a state and `tone` colours it, and neither is decided anywhere else in the console:
 *
 *   page   → the day's own state is Today's hero; worker-dead, the one other `page` state, is a
 *            takeover above Today (SPEC_5 Decision 3)
 *   strip  → a line under the header; the page below does not move
 *
 * `tone` maps to `--accept` / `--danger` / `--warn` / `--line-strong`. **Never to `--up`/`--down`**,
 * which are prices and nothing else: a reader of this product reads 紅 as up, and painting an alarm
 * in the same red as a rising price is the one confusion this console cannot afford.
 *
 * A state's `name` is rendered as a `data-state` attribute on its element. That is what
 * e2e/competition.spec.ts reads to assert the state is on screen, and it costs one attribute.
 */

import type { ReactNode } from "react";
import type { State, Tone } from "../../api/types/state";
import { useT } from "../../i18n/useT";
import { saysIn } from "../../lib/says";

const TONE_CLASS: Record<Tone, string> = {
  red: "--red",
  amber: "",
  green: "--quiet",
  neutral: "--quiet",
};

/** A takeover: the hazard edge first, so the level is legible before the colour is read. */
export function Takeover({ state, kicker, then }: { state: State; kicker?: string; then?: ReactNode }) {
  const { lang } = useT();
  const amber = state.tone === "amber";
  return (
    <div
      className={`takeover${amber ? " takeover--amber" : ""}`}
      data-state={state.name}
      data-level={state.level}
      data-tone={state.tone}
      role="alert"
    >
      <div className="takeover__stripe" aria-hidden="true" />
      <div className="takeover__body">
        <p className="takeover__kicker">{kicker ?? state.name}</p>
        <p className="takeover__what">{saysIn(state.says, lang)}</p>
        {then ? <div className="takeover__then">{then}</div> : null}
      </div>
    </div>
  );
}

/** A line inside the top strip. Neutral is the same shape for a fact that is not an alarm at all:
 *  a restart, a closed market. */
export function StripAlarm({ state, tag }: { state: State; tag?: string }) {
  const { lang } = useT();
  const suffix = TONE_CLASS[state.tone];
  return (
    <p
      className={`stripalarm${suffix ? ` stripalarm${suffix}` : ""}`}
      data-state={state.name}
      data-level={state.level}
      data-tone={state.tone}
    >
      <span className="stripalarm__tag">{tag ?? state.name}</span>
      <span>{saysIn(state.says, lang)}</span>
    </p>
  );
}

/** An inset rail inside the panel that owns the fact. */
export function RowAlarm({ state }: { state: State }) {
  const { lang } = useT();
  const suffix = TONE_CLASS[state.tone];
  return (
    <p
      className={`rowalarm${suffix ? ` rowalarm${suffix}` : ""}`}
      data-state={state.name}
      data-level="row"
      data-tone={state.tone}
    >
      <b>{state.name}</b>
      <span>{saysIn(state.says, lang)}</span>
    </p>
  );
}
